import { Injectable, signal } from '@angular/core';

export type MobilePlatform = 'ios' | 'android' | 'other';

/**
 * Detecta se o app está sendo acessado por um dispositivo móvel (iOS/Android),
 * usado para "travar" o site no celular e oferecer o download do app nativo.
 *
 * A detecção é feita uma única vez (o SO não muda durante a sessão) e exposta
 * como signal para consumo em templates zoneless.
 */
@Injectable({
    providedIn: 'root',
})
export class MobileDetectionService {
    /** Verdadeiro quando o acesso vem de um celular/tablet iOS ou Android. */
    public readonly isMobile = signal<boolean>(this._detectIsMobile());

    /** Plataforma detectada, usada para destacar a loja correta. */
    public readonly platform = signal<MobilePlatform>(this._detectPlatform());

    private _detectIsMobile(): boolean {
        // Override de QA: permite forçar/desligar a trava em desktop para testes.
        // Ex.: ?forceMobileGate=1 (liga) | ?forceMobileGate=0 (desliga).
        const override = this._readForceOverride();
        if (override !== null) return override;

        return this._isIOS() || this._isAndroid();
    }

    private _readForceOverride(): boolean | null {
        if (typeof window === 'undefined') return null;
        const href = window.location.href;
        if (/[?&#]forceMobileGate=1\b/.test(href)) return true;
        if (/[?&#]forceMobileGate=0\b/.test(href)) return false;
        return null;
    }

    private _detectPlatform(): MobilePlatform {
        if (this._isIOS()) return 'ios';
        if (this._isAndroid()) return 'android';
        return 'other';
    }

    private _isAndroid(): boolean {
        return /android/i.test(this._userAgent());
    }

    private _isIOS(): boolean {
        const ua = this._userAgent();
        if (/iphone|ipad|ipod/i.test(ua)) return true;
        // iPadOS 13+ se identifica como "MacIntel" com múltiplos toques.
        return (
            typeof navigator !== 'undefined' &&
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1
        );
    }

    private _userAgent(): string {
        return typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    }
}
